import api from './api';

export const authService = {
  signup: async (username, email, password) => {
    const response = await api.post('/auth/signup', { username, email, password });
    return response.data;
  },

  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

export const customerService = {
  searchByPhone: async (phone) => {
    const response = await api.get(`/customers/search?phone=${phone}`);
    return response.data;
  },

  createCustomer: async (name, phone, address) => {
    const response = await api.post('/customers', { name, phone, address });
    return response.data;
  },
};

export const orderService = {
  createOrder: async (orderData) => {
    const response = await api.post('/orders', orderData);
    return response.data;
  },

  getOrders: async () => {
    const response = await api.get('/orders');
    return response.data;
  },

  getOrder: async (orderId) => {
    const response = await api.get(`/orders/${orderId}`);
    return response.data;
  },
};

export const messageService = {
  getMessages: async (orderId) => {
    const response = await api.get(`/orders/${orderId}/messages`);
    return response.data;
  },

  sendMessage: async (orderId, content, type = 'text') => {
    const response = await api.post(`/orders/${orderId}/messages`, {
      content,
      type,
    });
    return response.data;
  },

  markAsRead: async (messageId) => {
    const response = await api.put(`/messages/${messageId}/read`);
    return response.data;
  },
};

// Extract a human-readable message from an axios/error object.
// FastAPI can return `detail` as a string OR a validation array/object — the
// latter previously showed up as "[object Object]" in toasts.
const errText = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || (typeof x === 'string' ? x : JSON.stringify(x))).join(', ');
  if (d && typeof d === 'object') return d.msg || JSON.stringify(d);
  if (err?.response?.status === 413) return 'File is too large to upload.';
  if (err?.message && err.message !== 'Network Error') return err.message;
  return '';
};

const CHUNK_THRESHOLD = 8 * 1024 * 1024; // files above 8MB use chunked upload
const CHUNK_SIZE = 5 * 1024 * 1024;       // 5MB per chunk

export const fileService = {
  uploadFile: async (file, onProgress) => {
    let fileToUpload = file;

    // Compress images before upload (like WhatsApp)
    const isImage = file.type.startsWith('image/');
    if (isImage) {
      try {
        fileToUpload = await compressImage(file);
      } catch (e) {
        console.warn('Image compression failed, uploading original:', e);
      }
    }

    // Large files (videos, big PDFs) → chunked upload to bypass proxy body limits.
    if (fileToUpload.size > CHUNK_THRESHOLD) {
      return await fileService.uploadFileChunked(fileToUpload, file.name || 'upload', onProgress);
    }

    const formData = new FormData();
    formData.append('file', fileToUpload);
    try {
      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (onProgress) onProgress(100);
      return response.data;
    } catch (err) {
      // Surface storage errors clearly — never silently drop the file
      const status = err?.response?.status;
      if (status === 503) {
        throw new Error(errText(err) || 'Storage service unavailable. Please try uploading again.');
      }
      throw new Error(errText(err) || 'Upload failed. Please check your connection and try again.');
    }
  },

  // Chunked upload: splits the file into parts, sends each to /upload/chunk,
  // then assembles + stores via /upload/complete. Reports 0-100% via onProgress.
  uploadFileChunked: async (file, filename, onProgress) => {
    const uploadId = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));

    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const chunk = file.slice(start, Math.min(start + CHUNK_SIZE, file.size));
      const fd = new FormData();
      fd.append('upload_id', uploadId);
      fd.append('chunk_index', String(i));
      fd.append('total_chunks', String(totalChunks));
      fd.append('file', chunk, filename);
      try {
        await api.post('/upload/chunk', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
        });
      } catch (err) {
        throw new Error(errText(err) || 'Upload failed while sending the file. Please try again.');
      }
      // Reserve the last 5% for the assemble/store step.
      if (onProgress) onProgress(Math.round(((i + 1) / totalChunks) * 95));
    }

    try {
      const res = await api.post('/upload/complete', {
        upload_id: uploadId,
        filename,
        content_type: file.type || 'application/octet-stream',
        total_chunks: totalChunks,
      }, { timeout: 120000 });
      if (onProgress) onProgress(100);
      return res.data;
    } catch (err) {
      throw new Error(errText(err) || 'Upload failed while finishing. Please try again.');
    }
  },
};

/**
 * Compress image client-side before upload.
 * Resizes to max 1200px on longest side, JPEG quality 70%.
 * ~3MB original → ~100-200KB compressed.
 */
const compressImage = (file) => {
  return new Promise((resolve, reject) => {
    const maxSize = 1200;
    const quality = 0.7;

    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.onload = () => {
        let { width, height } = img;

        // Resize if larger than maxSize
        if (width > maxSize || height > maxSize) {
          if (width > height) {
            height = Math.round((height * maxSize) / width);
            width = maxSize;
          } else {
            width = Math.round((width * maxSize) / height);
            height = maxSize;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressed = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressed);
            } else {
              reject(new Error('Compression failed'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// Production workflow services
export const productionService = {
  getStages: async () => {
    const response = await api.get('/production/stages');
    return response.data;
  },

  getWorkers: async () => {
    const response = await api.get('/users/by-role/worker');
    return response.data;
  },

  recommendWorker: async (orderId, workerId, notes) => {
    const response = await api.post(`/orders/${orderId}/recommend-worker`, {
      worker_id: workerId,
      notes,
    });
    return response.data;
  },

  assignWorker: async (orderId, workerId, notes) => {
    const response = await api.post(`/orders/${orderId}/assign-worker`, {
      worker_id: workerId,
      notes,
    });
    return response.data;
  },

  changeStage: async (orderId, newStage, notes) => {
    const response = await api.post(`/orders/${orderId}/change-stage`, {
      new_stage: newStage,
      notes,
    });
    return response.data;
  },

  qcApprove: async (orderId, approved, notes) => {
    const response = await api.post(`/orders/${orderId}/qc-approve`, {
      approved,
      notes,
    });
    return response.data;
  },

  issueMaterial: async (orderId, materialType, weight, count, notes) => {
    const response = await api.post(`/orders/${orderId}/issue-material`, {
      material_type: materialType,
      weight,
      count,
      notes,
    });
    return response.data;
  },

  getPendingWorkerAssignment: async () => {
    const response = await api.get('/orders/pending/worker-assignment');
    return response.data;
  },

  getPendingQC: async () => {
    const response = await api.get('/orders/pending/qc-approval');
    return response.data;
  },
};

// Admin services
export const adminService = {
  getAllUsers: async () => {
    const response = await api.get('/admin/users');
    return response.data;
  },

  createUser: async (userData) => {
    const response = await api.post('/admin/users', userData);
    return response.data;
  },

  updateUserRole: async (userId, role, department) => {
    const response = await api.put(`/admin/users/${userId}/role`, {
      role,
      department,
    });
    return response.data;
  },
};


export const estimateService = {
  createEstimate: async (estimateData) => {
    const response = await api.post('/estimates', estimateData);
    return response.data;
  },

  getEstimates: async (phone = null) => {
    const url = phone ? `/estimates?phone=${phone}` : '/estimates';
    const response = await api.get(url);
    return response.data;
  },

  getEstimatesGroupedByCustomer: async (status = null) => {
    const url = status ? `/estimates/customers?status=${status}` : '/estimates/customers';
    const response = await api.get(url);
    return response.data;
  },

  getEstimate: async (estimateNumber) => {
    const response = await api.get(`/estimates/${estimateNumber}`);
    return response.data;
  },

  deleteEstimate: async (estimateNumber) => {
    const response = await api.delete(`/estimates/${estimateNumber}`);
    return response.data;
  },

  confirmEstimate: async (estimateNumber) => {
    const response = await api.patch(`/estimates/${estimateNumber}/confirm`);
    return response.data;
  },

  duplicateEstimate: async (estimateNumber) => {
    const response = await api.post(`/estimates/${estimateNumber}/duplicate`);
    return response.data;
  },
};
